import { useBooleanState } from '@cian/react-utils';
import { Button } from '@cian/ui-kit';

import { memo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { type RoomsInfoItem } from 'shared/components/RoomsList';
import { getAllocatedResidentialComplexes } from 'shared/selectors/residentialComplexes/getAlloсatedResidentialComplexes';
import {
  DEFAULT_NUMBER_OF_COMPLEXES_PER_PAGE,
  PIK_PLUS_NUMBER_OF_COMPLEXES_PER_PAGE,
} from 'shared/settings/residentialComplexes';
import { checkPikPlusResidentialComplexes } from 'shared/utils/checkPikPlusResidentialComplexes';
import { extractUndefined } from 'shared/utils/extractUndefined';

import { getResidentialComplexImageUrl } from '../../utils/getResidentialComplexImageUrl';
import { ResidentialComplexesCardDefault } from '../ResidentialComplexesCardDefault';
import { ResidentialComplexesCardSeriaPlus } from '../ResidentialComplexesCardSeriaPlus';

import { ResidentialComplexesList } from './ResidentialComplexesList';

const defaultRoomsInfo = Array.of<RoomsInfoItem>();

export const ResidentialComplexesListContainer = memo(function ResidentialComplexesListContainer() {
  const residentialComplexes = useSelector(getAllocatedResidentialComplexes, shallowEqual);

  const complexesNumber = checkPikPlusResidentialComplexes(residentialComplexes)
    ? PIK_PLUS_NUMBER_OF_COMPLEXES_PER_PAGE
    : DEFAULT_NUMBER_OF_COMPLEXES_PER_PAGE;
  const { state: showAll, setTrue: setShowAll } = useBooleanState();
  const hasMoreComplexes = !showAll && residentialComplexes.length > complexesNumber;
  const complexesForRender = showAll ? residentialComplexes : residentialComplexes.slice(0, complexesNumber);

  return (
    <ResidentialComplexesList
      complexesList={complexesForRender.map(complex => {
        const roomsInfo = complex.roomsInfo || defaultRoomsInfo;

        const imageUrl = getResidentialComplexImageUrl(complex);

        const subtitle = extractUndefined(complex.finishInfo?.label);

        const promoLabel = extractUndefined(complex.promoLabel);

        return complex.displayType === 'plus' ? (
          <ResidentialComplexesCardSeriaPlus
            imageUrl={imageUrl}
            key={complex.id}
            promoLabel={promoLabel}
            roomsInfo={roomsInfo}
            subtitle={subtitle}
            title={complex.title}
            transports={complex.transports}
            url={complex.publicUrl}
          />
        ) : (
          <ResidentialComplexesCardDefault
            imageUrl={imageUrl}
            key={complex.id}
            promoLabel={promoLabel}
            roomsInfo={roomsInfo}
            subtitle={subtitle}
            title={complex.title}
            transports={complex.transports}
            url={complex.publicUrl}
          />
        );
      })}
      showMoreButton={
        hasMoreComplexes && (
          <Button
            data-testid="residential-complexes-list-show-more-button"
            size="M"
            theme="fill_secondary"
            onClick={setShowAll}
          >
            Ещё ЖК
          </Button>
        )
      }
    />
  );
});
